import { type ReactNode, useId } from "react";

import { cn } from "./cn";

type FieldProps = {
  label: string;
  /** Shown under the control; replaced by `error` when there is one. */
  hint?: ReactNode;
  error?: string | null;
  /** e.g. "42 / 60" — rendered right-aligned next to the label. */
  counter?: { value: number; max: number };
  className?: string;
  children: (props: {
    id: string;
    "aria-invalid": boolean;
    "aria-describedby"?: string;
  }) => ReactNode;
};

/** Label + control + hint/error, wired for accessibility. */
export const Field = ({ label, hint, error, counter, className, children }: FieldProps) => {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-xs font-medium text-ink">
          {label}
        </label>
        {counter && (
          <span
            className={cn(
              "text-[11px] tabular-nums",
              counter.value > counter.max ? "text-red-600" : "text-muted",
            )}
          >
            {counter.value} / {counter.max}
          </span>
        )}
      </div>
      {children({
        id,
        "aria-invalid": Boolean(error),
        ...(message ? { "aria-describedby": messageId } : {}),
      })}
      {message && (
        <p id={messageId} className={cn("text-xs", error ? "text-red-600" : "text-muted")}>
          {message}
        </p>
      )}
    </div>
  );
};
